import React, { FC } from 'react';
import Link from 'next/link';
import { PageLocalizedEntity } from 'src/modules/pages';
import { ProductCategoryLocalizedEntity } from 'src/modules/products';
import { Logo } from 'src/modules/layout/components/header/components/Logo';
import styles from './LayoutFooterMobile.module.scss';
import { Collapse, Row, Col } from 'antd';
import { GlobalIcon, IconImage, GlobalNewsletterButton } from 'src/modules/global';
import { Locale } from 'src/modules/locale';
import { FormattedMessage } from 'react-intl';
import { ArticleCategoryLocalizedEntity } from 'src/modules/articles';
import { OutputChannels, useLocale } from 'src/modules/locale';

import getSocialMediaHref from 'public/config/getSocialMediaHref';

export interface LayoutFooterMobileProps {
  professionalsPage: PageLocalizedEntity;
  aboutPage: PageLocalizedEntity;
  productCategories: ProductCategoryLocalizedEntity[];
  articleCategories: ArticleCategoryLocalizedEntity[];
  setLocale: (locale: string) => void;
  locale: Locale;
  usaUrl: string;
  europeUrl: string;
  australiaUrl: string;
}

const splitInTwo = (arr: any[]) => {
  const halfIndex = Math.ceil(arr.length / 2);
  return [arr.slice(0, halfIndex), arr.slice(halfIndex)];
};

export const LayoutFooterMobile: FC<LayoutFooterMobileProps> = ({
  productCategories,
  professionalsPage,
  articleCategories,
  aboutPage,
  locale,
}) => {
  const { outputChannel } = useLocale();
  const showPinterest = outputChannel !== OutputChannels.US;

  return (
    <div className={styles.container}>
      <Logo size="large" className={styles.logo} />

      <Collapse
        className={styles.collapseCategoryContainer}
        bordered={false}
        expandIconPosition="right"
        expandIcon={({ isActive }) => (
          <GlobalIcon image={IconImage.DownArrow} rotation={isActive ? 180 : 0} />
        )}>
        <Collapse.Panel
          collapsible="header"
          header={
            <div className={styles.collapsibleHeaderButton}>
              <FormattedMessage id="header/menu_button/products" />
            </div>
          }
          key={`panel-products`}>
          <div className={styles.collapsiblePanelInnerContainer}>
            <Row>
              {splitInTwo(productCategories).map((half, i) => (
                <Col span={12} key={`col-${i}`}>
                  {half.map((pc) => (
                    <div key={`product-category-${pc.id}`}>
                      <Link href={`/products/${encodeURIComponent(pc.slug)}`}>
                        <a className={styles.link}>{pc.name}</a>
                      </Link>
                    </div>
                  ))}
                </Col>
              ))}
            </Row>
          </div>
        </Collapse.Panel>
        <Collapse.Panel
          collapsible="header"
          header={
            <div className={styles.collapsibleHeaderButton}>
              {' '}
              <FormattedMessage id="header/menu_button/about" />
            </div>
          }
          key={`panel-about`}>
          <div className={styles.collapsiblePanelInnerContainer}>
            <Row>
              {!!aboutPage?.dynamic_content?.length &&
                splitInTwo(aboutPage?.dynamic_content).map((half, i) => (
                  <Col span={12} key={`col-${i}`}>
                    {half.map((dc, idx) => (
                      <div key={`about-link-${idx}`}>
                        <Link href={`/about/${encodeURIComponent(dc.link)}`}>
                          <a className={styles.link}>{dc.title}</a>
                        </Link>
                      </div>
                    ))}
                  </Col>
                ))}
            </Row>
          </div>
        </Collapse.Panel>
        <Collapse.Panel
          collapsible="header"
          header={
            <div className={styles.collapsibleHeaderButton}>
              <FormattedMessage id="header/menu_button/professionals" />
            </div>
          }
          key={`panel-professionals`}>
          <div className={styles.collapsiblePanelInnerContainer}>
            <Row>
              {!!professionalsPage?.dynamic_content?.length &&
                splitInTwo(professionalsPage?.dynamic_content).map((half, i) => (
                  <Col span={12} key={`col-${i}`}>
                    {half.map((dc, idx) => (
                      <div key={`professionals-link-${idx}`}>
                        <Link href={`/professionals/${encodeURIComponent(dc.link)}`}>
                          <a className={styles.link}>{dc.title}</a>
                        </Link>
                      </div>
                    ))}
                  </Col>
                ))}
            </Row>
          </div>
        </Collapse.Panel>
        <Collapse.Panel
          collapsible="header"
          header={
            <div className={`${styles.collapsibleHeaderButton} ${styles.borderless}`}>
              <FormattedMessage id="footer/inspiration" />
            </div>
          }
          key={`panel-inspiration`}>
          <div className={styles.collapsiblePanelInnerContainer}>
            <div>
              <Link href={'/magazine'}>
                <a className={styles.link}>
                  <FormattedMessage id="page/magazine/listing/all_topics" />
                </a>
              </Link>
            </div>
            {articleCategories.map((ac, idx) => (
              <div key={idx}>
                <Link href={`/magazine?category=${encodeURIComponent(ac.name)}`}>
                  <a className={styles.link}>{ac.name}</a>
                </Link>
              </div>
            ))}
            <div>
              <Link href={'/magazine/instagram'}>
                <a className={styles.link}>
                  <FormattedMessage id="breadcrumbs/magazine/instagram" />
                </a>
              </Link>
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>

      <GlobalNewsletterButton showInfoText={true} />

      <div className={`${styles.infoContainer} ${styles.socialLinksAndLocales}`}>
        <a href={getSocialMediaHref(locale, 'facebook')} className={styles.socialLink}>
          <GlobalIcon image={IconImage.Facebook} />
        </a>
        <a href={getSocialMediaHref(locale, 'linkedin')} className={styles.socialLink}>
          <GlobalIcon image={IconImage.Linkedin} />
        </a>
        <a href={getSocialMediaHref(locale, 'instagram')} className={styles.socialLink}>
          <GlobalIcon image={IconImage.Instagram} />
        </a>
        <a href={getSocialMediaHref(locale, 'youtube')} className={styles.socialLink}>
          <GlobalIcon image={IconImage.Youtube} />
        </a>
        {showPinterest && (
          <a href={getSocialMediaHref(locale, 'pinterest')} className={styles.socialLink}>
            <GlobalIcon image={IconImage.Pinterest} />
          </a>
        )}
      </div>
      <div className={styles.infoContainer}>
        <Row>
          <Col span={8} className={styles.legalLinkContainer}>
            <Link href="/imprint">
              <a className={styles.legalLink}>
                <FormattedMessage id="footer/imprint" />
              </a>
            </Link>
          </Col>
          <Col span={8} className={styles.legalLinkContainer}>
            <Link href="/terms">
              <a className={styles.legalLink}>
                <FormattedMessage id="footer/terms" />
              </a>
            </Link>
          </Col>
          <Col span={8} className={styles.legalLinkContainer}>
            <Link href="/privacy">
              <a className={styles.legalLink}>
                <FormattedMessage id="footer/privacy" />
              </a>
            </Link>
          </Col>
        </Row>
        <div className={styles.copyright}>
          <FormattedMessage id="footer/copyright" />
        </div>
      </div>
    </div>
  );
};
